/**
 * UploadSampleStep - Step 1: Upload sample file for analysis
 * @module components/starnexus/wizard/steps/UploadSampleStep
 *
 */

import React, { useCallback } from 'react';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import FormField from '@amzn/awsui-components-console/form-field';
import FileUpload from '@amzn/awsui-components-console/file-upload';
import Table from '@amzn/awsui-components-console/table';
import Box from '@amzn/awsui-components-console/box';
import Alert from '@amzn/awsui-components-console/alert';
import Select from '@amzn/awsui-components-console/select';
import type { Team } from '../../../../types/starnexus/team';

const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB
const ALLOWED_EXTENSIONS = ['.csv', '.json', '.xlsx', '.xls'];

interface UploadSampleStepProps {
  file: File | null;
  preview: string[][] | null;
  error: string | null;
  onFileChange: (file: File | null, preview: string[][] | null, error: string | null) => void;
  teams?: Team[];
  activeTeam?: Team | null;
  selectedTeamId?: string;
  onTeamChange?: (teamId: string) => void;
  isSuperUser?: boolean;
  sheetName?: string | null;
  onSheetChange?: (sheetName: string) => void;
}

export const UploadSampleStep: React.FC<UploadSampleStepProps> = ({ file, preview, error, onFileChange, teams = [], activeTeam = null, selectedTeamId = '', onTeamChange, isSuperUser = false, sheetName, onSheetChange }) => {
  const [sheetNames, setSheetNames] = React.useState<string[]>([]);
  const [workbookBuffer, setWorkbookBuffer] = React.useState<ArrayBuffer | null>(null);
  // Validate file
  const validateFile = useCallback((f: File): string | null => {
    // Check file size
    if (f.size > MAX_FILE_SIZE) {
      return `File size exceeds 30MB limit. Current size: ${(f.size / 1024 / 1024).toFixed(2)}MB`;
    }

    // Check file extension
    const ext = '.' + f.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `Invalid file format. Allowed formats: CSV, JSON, Excel (.xlsx, .xls)`;
    }

    return null;
  }, []);

  // Parse CSV with the robust XLSX parser (handles quoted commas, BOM, CRLF) and
  // return ALL rows. Sampling is applied separately by sampleRows so we can
  // spread the sample across the whole file instead of just the head.
  const parseCSV = async (content: string): Promise<string[][]> => {
    const XLSX = await import('xlsx');
    const wb = XLSX.read(content, { type: 'string', raw: true });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false, blankrows: false }) as any[][];
    return rows.map((r) => r.map((c) => String(c ?? '')));
  };

  // Representative sample: header + first rows + evenly-spaced rows across the
  // rest of the file, so sparse columns (values only deep in the file) and late
  // type changes are detected, not just the head. Returns at most `max` data rows.
  const sampleRows = (rows: string[][], max = 200): string[][] => {
    if (rows.length <= 1) return rows;
    const [header, ...data] = rows;
    if (data.length <= max) return rows;
    const head = Math.min(50, max);
    const taken = data.slice(0, head);
    const rest = data.slice(head);
    const stride = Math.max(1, Math.ceil(rest.length / (max - head)));
    for (let i = 0; i < rest.length && taken.length < max; i += stride) taken.push(rest[i]);
    return [header, ...taken];
  };

  // Parse JSON content into all rows (sampling applied by caller)
  const parseJSON = (content: string): string[][] => {
    try {
      const data = JSON.parse(content);
      const arr = Array.isArray(data) ? data : [data];
      if (arr.length === 0) return [];

      const headers = Object.keys(arr[0]);
      const rows = arr.map((item) => headers.map((h) => String(item[h] ?? '')));
      return [headers, ...rows];
    } catch {
      return [];
    }
  };

  // Handle file selection
  const handleFileChange = useCallback(
    async (files: File[]) => {
      if (files.length === 0) {
        onFileChange(null, null, null);
        return;
      }

      const f = files[0];
      const validationError = validateFile(f);

      if (validationError) {
        onFileChange(f, null, validationError);
        return;
      }

      // Read and parse file for preview
      try {
        const ext = f.name.split('.').pop()?.toLowerCase();
        let previewData: string[][] = [];

        if (ext === 'csv') {
          const content = await f.text();
          previewData = sampleRows(await parseCSV(content));
        } else if (ext === 'json') {
          const content = await f.text();
          previewData = sampleRows(parseJSON(content));
        } else if (ext === 'xlsx' || ext === 'xls') {
          const XLSX = await import('xlsx');
          const buffer = await f.arrayBuffer();
          const wb = XLSX.read(buffer, { type: 'array' });
          if (wb.SheetNames.length > 1) {
            setSheetNames(wb.SheetNames);
            setWorkbookBuffer(buffer);
            if (onSheetChange) onSheetChange(wb.SheetNames[0]);
          } else {
            setSheetNames([]);
            setWorkbookBuffer(null);
          }
          const selectedSheet = wb.SheetNames[0];
          const ws = wb.Sheets[selectedSheet];
          const rows: string[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
          previewData = sampleRows(rows.map(row => row.map(cell => String(cell))));
        }

        if (previewData.length === 0) {
          onFileChange(f, null, 'Unable to parse file content. Please check the file format.');
          return;
        }

        onFileChange(f, previewData, null);
      } catch (err) {
        onFileChange(f, null, 'Error reading file. Please try again.');
      }
    },
    [validateFile, onFileChange, onSheetChange]
  );

  // Handle sheet selection change for multi-sheet Excel files
  const handleSheetChange = useCallback(async (selectedSheetName: string) => {
    if (!workbookBuffer) return;
    if (onSheetChange) onSheetChange(selectedSheetName);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(workbookBuffer, { type: 'array' });
      const ws = wb.Sheets[selectedSheetName];
      const rows: string[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      const previewData = sampleRows(rows.map(row => row.map(cell => String(cell))));
      if (file) onFileChange(file, previewData, null);
    } catch { /* keep current preview */ }
  }, [workbookBuffer, file, onFileChange, onSheetChange]);

  // Generate column definitions from preview headers
  const columnDefinitions =
    preview && preview.length > 0
      ? preview[0].map((header, idx) => ({
          id: `col-${idx}`,
          header: header || `Column ${idx + 1}`,
          cell: (item: string[]) => item[idx] || '-'
        }))
      : [];

  // Get data rows (skip header)
  const dataRows = preview && preview.length > 1 ? preview.slice(1) : [];

  return (
    <SpaceBetween size="m">
      {/* Team selector: shown when user belongs to multiple teams OR is super-user */}
      {(teams.length > 1 || (teams.length === 1 && isSuperUser)) && (
        <Container header={<Header variant="h3">Select team</Header>}>
          <FormField
            label="Team"
            description="The team that will own this dataset"
          >
            <Select
              selectedOption={
                teams.find((t) => t.teamId === selectedTeamId)
                  ? { value: selectedTeamId, label: teams.find((t) => t.teamId === selectedTeamId)!.teamName }
                  : activeTeam
                    ? { value: activeTeam.teamId, label: activeTeam.teamName }
                    : null
              }
              onChange={({ detail }) => {
                if (detail.selectedOption.value && onTeamChange) {
                  onTeamChange(detail.selectedOption.value);
                }
              }}
              options={teams.map((t) => ({ value: t.teamId, label: t.teamName }))}
              placeholder="Select a team"
              ariaLabel="Select team for dataset"
            />
          </FormField>
        </Container>
      )}

      {/* Single team: auto-filled, show info text (not for super-users who always get dropdown) */}
      {teams.length === 1 && activeTeam && !isSuperUser && (
        <Container header={<Header variant="h3">Team</Header>}>
          <Box variant="p">
            This dataset will be created under team <Box variant="span" fontWeight="bold">{activeTeam.teamName}</Box>.
          </Box>
        </Container>
      )}

      <Container header={<Header variant="h3">Select your data file</Header>}>
        <SpaceBetween size="l">
          <FormField
            label="Sample data file"
            description="Choose a file to analyze. Our AI will automatically detect the schema and generate configuration."
          >
            <SpaceBetween size="s">
              <FileUpload
              onChange={({ detail }) => handleFileChange(detail.value)}
              value={file ? [file] : []}
              i18nStrings={{
                uploadButtonText: (e) => (e ? 'Choose files' : 'Choose file'),
                dropzoneText: (e) => (e ? 'Drop files to upload' : 'Drop file to upload'),
                removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                limitShowFewer: 'Show fewer files',
                limitShowMore: 'Show more files',
                errorIconAriaLabel: 'Error'
              }}
              accept=".csv,.json,.xlsx,.xls"
              showFileLastModified
              showFileSize
              tokenLimit={1}
            />
              <Box variant="small" color="text-body-secondary">Supported formats: CSV, JSON, Excel (.xlsx, .xls). Maximum size: 10MB.</Box>
            </SpaceBetween>
          </FormField>

          {error && (
            <Alert type="error" header="File validation error">
              {error}
            </Alert>
          )}

          {sheetNames.length > 1 && (
            <FormField label="Excel sheet" description={`This workbook has ${sheetNames.length} sheets. Select which one to use.`}>
              <Select
                selectedOption={sheetName ? { value: sheetName, label: sheetName } : { value: sheetNames[0], label: sheetNames[0] }}
                onChange={({ detail }) => { if (detail.selectedOption.value) handleSheetChange(detail.selectedOption.value); }}
                options={sheetNames.map(s => ({ value: s, label: s }))}
              />
            </FormField>
          )}
        </SpaceBetween>
      </Container>

      {preview && preview.length > 0 && (
        <Container
          header={
            <Header variant="h2" description={`Showing first ${Math.min(dataRows.length, 10)} of ${dataRows.length} sampled rows`}>
              File preview
            </Header>
          }
        >
          <Table
            columnDefinitions={columnDefinitions}
            items={dataRows.slice(0, 10)}
            variant="embedded"
            empty={
              <Box textAlign="center" color="inherit">
                <b>No data</b>
                <Box variant="p" color="inherit">
                  The file appears to be empty.
                </Box>
              </Box>
            }
          />
        </Container>
      )}
    </SpaceBetween>
  );
};

export default UploadSampleStep;
